import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'

import { invoicingService } from '@/api/invoicing'
import type { InvoiceStatus, ClientInvoiceListItem } from '@/api/invoicing'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { columns } from './components/invoices-columns'
import { InvoicesToolbar } from './components/invoices-toolbar'
import { InvoiceCreateDialog } from './components/invoice-create-dialog'

export interface InvoicesTableParams {
  page: number
  page_size: number
  status?: InvoiceStatus
  client?: string       // UUID sent to API
  client_name?: string  // display-only, kept in sync with client
  ordering?: string
}

export function InvoiceList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)

  const [params, setParams] = useState<InvoicesTableParams>({
    page: 0,
    page_size: 20,
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['invoices', params],
    queryFn: () =>
      invoicingService.list({
        page: params.page,
        page_size: params.page_size,
        status: params.status,
        client: params.client,
        ordering: params.ordering,
      }),
  })

  const pages = Math.ceil((data?.count ?? 0) / params.page_size) || 1

  const handleRowClick = (row: ClientInvoiceListItem) => {
    // @ts-ignore
    navigate({ to: `/invoices/${row.id}` })
  }

  return (
    <Main>
      <PageHeader
        leading={
          <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </span>
        }
        title={t('facturation')}
        description={t('facturation_desc')}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <FileText className="me-2 h-4 w-4" />
            {t('inv_create')}
          </Button>
        }
      />

      <div className="-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0">
        <div className="space-y-6">
          {isLoading ? (
            <DataTableSkeleton columns={8} rows={10} />
          ) : (
            <DataTable
              columns={columns}
              data={data?.results ?? []}
              pages={pages}
              params={params}
              setParams={setParams}
              ToolbarName={InvoicesToolbar}
              storageKey="invoices-table"
              onRowClick={handleRowClick}
            />
          )}
        </div>
      </div>

      <InvoiceCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          refetch()
          setCreateOpen(false)
        }}
      />
    </Main>
  )
}
